import { ensureArray } from 'lib/util/array';
import { env } from 'lib/env';

/**
 * Normalizes a redirect url to protect against XSS or blind redirect attacks
 * @param theUrl The url to normalize
 * @param defaultValue The url to return if the input url is invalid
 */
export const normalizeRedirectUrl = (
  theUrl: string | string[] | undefined,
  defaultValue: string | undefined = undefined
): string => {
  // Normalize input
  const inputUrl = ensureArray(theUrl || '/')[0].trim();
  const checkUrl = inputUrl.toLocaleLowerCase();
  // Is it either site-relative?
  if (checkUrl.startsWith('/')) {
    return inputUrl;
  }
  // or absolute scoped to our site ?
  const normalizedPublicUrl = `${env.PUBLIC_URL}/`;
  if (checkUrl.startsWith(normalizedPublicUrl)) {
    return inputUrl;
  }
  // If we made it here we are not valid
  return defaultValue || '/';
};

export const getPublicHostName = () => new URL(`${env.PUBLIC_URL}`).host;
export const getAbsolutePath = (url: string | URL): string => {
  if (!url) {
    return url;
  }
  const isString = typeof url === 'string';
  const asString = isString ? url : url.toString();
  const asUrl = new URL(asString, env.PUBLIC_URL);
  return asUrl.toString();
};

const sanitizeQueryParamValue = (rawValue: string | null | undefined): string | undefined => {
  if (rawValue == null) {
    return undefined;
  }

  const sanitizedValue = Array.from(rawValue)
    .filter((ch) => {
      const code = ch.codePointAt(0) ?? 0;
      return (code > 0x1f && code !== 0x7f) || code > 0x7f;
    })
    .join('')
    .replace(/[<>"'`]/g, '')
    .trim();

  return sanitizedValue.length > 0 ? sanitizedValue : undefined;
};

export const getWithoutCase = (params: URLSearchParams, paramName: string): string | undefined => {
  const checkFor = paramName?.toLocaleLowerCase() ?? '';
  const keyName = Array.from(params.keys()).find((key) => key.toLowerCase() === checkFor);

  if (!keyName) {
    return undefined;
  }

  return sanitizeQueryParamValue(params.get(keyName));
};

export const getAllWithPrefix = (
  params: URLSearchParams,
  prefix: string
): Record<string, string> => {
  const checkPrefix = prefix?.toLocaleLowerCase() ?? '';
  if (!checkPrefix) {
    return {};
  }

  return Array.from(new Set(Array.from(params.keys()))).reduce((ret, key) => {
    if (!key.toLocaleLowerCase().startsWith(checkPrefix)) {
      return ret;
    }
    const value = params.get(key);
    if (value !== null) {
      return {
        ...ret,
        [key]: value,
      };
    }
    return ret;
  }, {} as Record<string, string>);
};

export type Falsy = false | 0 | '' | 'false' | null | undefined;

// this is a type predicate - if x is `truthy`, then it's T
export const isTruthy = <T>(x: T | Falsy): x is T => !!x && x !== 'false';
